import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/lunelle/Logo";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { AdminTable, Stat } from "@/components/admin/AdminTable";
import { CreatorsTable } from "@/components/admin/CreatorsTable";
import { adminMe, adminWaitlist, adminLogout, downloadCsv, adminCreators } from "@/lib/admin-api";
import { usePageMeta } from "@/lib/usePageMeta";

export default function AdminPage() {
  usePageMeta("Admin | Lunelle", "Private Lunelle team dashboard.");
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [creators, setCreators] = useState(null);
  const [tab, setTab] = useState("waitlist");

  const load = () => {
    adminWaitlist().then(setData).catch(() => toast.error("Couldn't load sign-ups"));
    adminCreators().then(setCreators).catch(() => toast.error("Couldn't load creators"));
  };

  useEffect(() => {
    adminMe().then(setUser).catch(() => setUser(false));
  }, []);
  useEffect(() => {
    if (user) load();
  }, [user]);

  const logout = async () => {
    await adminLogout().catch(() => null);
    setUser(false);
    setData(null);
  };

  const stats = data?.stats;
  const topFaith = stats ? Object.entries(stats.faiths ?? {}).sort((a, b) => b[1] - a[1])[0] : null;
  return (
    <div className="min-h-screen" data-testid="admin-page">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-5 py-6 sm:px-8 lg:px-12">
        <Link to="/"><Logo /></Link>
        {user && (
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-button" className="flex h-10 items-center gap-2 rounded-full border border-[#C86D51]/25 px-4 text-sm text-[#2C221E] hover:border-[#9E4429]"><RefreshCw className="h-4 w-4" /><span className="hidden sm:inline">Refresh</span></button>
            <button onClick={() => downloadCsv().catch(() => toast.error("Export failed"))} data-testid="admin-export-csv-button" className="flex h-10 items-center gap-2 rounded-full bg-[#2C221E] px-4 text-sm text-[#FBF5EE] hover:bg-[#9E4429]"><Download className="h-4 w-4" />Export CSV</button>
            <button onClick={logout} data-testid="admin-logout-button" aria-label="Sign out" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"><LogOut className="h-4 w-4" /></button>
          </div>
        )}
      </header>
      <main className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:px-12">
        {user === null && <p className="py-20 text-center text-[#6B5A52]">Loading…</p>}
        {user === false && <div className="py-16"><AdminLogin onDone={setUser} /></div>}
        {user && (
          <>
            <div className="mb-8 inline-flex rounded-full border border-[#C86D51]/20 bg-white/70 p-1" role="tablist">
              {[["waitlist", "Waitlist"], ["creators", `Creators${creators?.stats?.pending ? ` (${creators.stats.pending} new)` : ""}`]].map(([id, l]) => (
                <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === id ? "bg-[#2C221E] text-[#FBF5EE]" : "text-[#5C4A42] hover:text-[#2C221E]"}`}>{l}</button>
              ))}
            </div>
            {tab === "creators" ? (
              <>
                <h1 className="font-serif text-5xl font-light text-[#2C221E]">Creators</h1>
                <div className="mt-8 grid grid-cols-3 gap-3">
                  <Stat label="Waiting for review" value={creators?.stats?.pending ?? "—"} testId="admin-stat-creators-pending" />
                  <Stat label="Approved" value={creators?.stats?.approved ?? "—"} testId="admin-stat-creators-approved" />
                  <Stat label="Sign-ups from creators" value={creators?.stats?.signups ?? "—"} testId="admin-stat-creators-signups" />
                </div>
                <div className="mt-8"><CreatorsTable rows={Array.isArray(creators?.rows) ? creators.rows : []} onChange={load} /></div>
              </>
            ) : (
          <>
            <h1 className="font-serif text-5xl font-light text-[#2C221E]">Waitlist</h1>
            <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat label="Sign-ups" value={stats?.total ?? "—"} testId="admin-stat-total" />
              <Stat label="Confirmed" value={stats?.confirmed ?? "—"} testId="admin-stat-confirmed" />
              <Stat label="Joined via invite" value={stats?.invited ?? "—"} testId="admin-stat-invited" />
              <Stat label="Top faith" value={topFaith ? topFaith[0][0].toUpperCase() + topFaith[0].slice(1) : "—"} testId="admin-stat-faith" />
            </div>
            <div className="mt-8"><AdminTable rows={Array.isArray(data?.rows) ? data.rows : []} /></div>
          </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
